import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, Link2Off } from "lucide-react";
import InviktusLogo from "@/components/InviktusLogo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { getPublicDemo, API_BASE, publicFlowUrl } from "@/lib/api";
import { useNarration } from "@/hooks/useNarration";
import { NarrationBar } from "@/components/demo-player/NarrationBar";
import { PlayerInterview } from "@/components/demo-player/PlayerInterview";
import { PlayerReport } from "@/components/demo-player/PlayerReport";
import { PlayerAgents } from "@/components/demo-player/PlayerAgents";
import { PlayerChat } from "@/components/demo-player/PlayerChat";
import { PlayerMetrics } from "@/components/demo-player/PlayerMetrics";
import { PlayerKbl } from "@/components/demo-player/PlayerKbl";
import { PlayerMcps } from "@/components/demo-player/PlayerMcps";
import { PlayerDashboard } from "@/components/demo-player/PlayerDashboard";
import { FlowDocumentViewer } from "@/components/FlowDocumentViewer";

const ADVANCE_DELAY_MS = 1200;

const VIEWS = {
  interview: (d) => <PlayerInterview interview={d.interview} />,
  report: (d) => <PlayerReport report={d.report} />,
  flow: (d, token) => <FlowDocumentViewer pngUrl={publicFlowUrl(token, "png")} pdfUrl={publicFlowUrl(token, "pdf")} />,
  agents: (d) => <PlayerAgents agents={d.agents} />,
  mcps: (d) => <PlayerMcps mcps={d.mcps} />,
  kbl: (d) => <PlayerKbl docs={d.kbl} />,
  chat: (d) => <PlayerChat chat={d.chat} />,
  metrics: (d) => <PlayerMetrics metrics={d.metrics} />,
  dashboard: (d) => <PlayerDashboard dashboard={d.dashboard} />,
};

const StepView = ({ step, data, token }) => VIEWS[data.steps[step].key](data, token);

export default function DemoPlayer() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [step, setStep] = useState(0);
  const [auto, setAuto] = useState(false);
  const timer = useRef(null);
  const top = useRef(null);

  useEffect(() => {
    getPublicDemo(token)
      .then(setData)
      .catch((e) => setError(e?.response?.data?.detail || "No se pudo abrir la demo."));
  }, [token]);

  const total = data?.steps.length || 5;
  const goTo = useCallback((i) => {
    clearTimeout(timer.current);
    setStep(((i % total) + total) % total);
    top.current?.scrollIntoView({ behavior: "smooth" });
  }, [total]);

  const onEnded = () => {
    if (auto) timer.current = setTimeout(() => goTo(step + 1), ADVANCE_DELAY_MS);
  };
  const { playing, toggle } = useNarration({
    src: data?.voice === "ai" ? `${API_BASE.replace(/\/api$/, "")}${data.steps[step].audio_url}` : null,
    text: data?.steps[step].narration,
    auto,
    onEnded,
  });
  useEffect(() => () => clearTimeout(timer.current), []);

  if (error) return <PlayerMessage icon={Link2Off} text={error} />;
  if (!data) return <PlayerMessage icon={Loader2} spin text="Cargando demo…" />;

  return (
    <div className="min-h-screen bg-slate-50 pb-40 text-slate-900" data-testid="demo-player">
      <header ref={top} className="bg-[#0B1437] text-white">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-4 px-4 py-4 sm:px-6">
          <div className="flex items-center gap-3">
            <InviktusLogo size={32} tone="light" />
            <span className="hidden text-[10px] uppercase tracking-[0.2em] text-slate-400 sm:inline">Demo interactiva · solo lectura</span>
          </div>
          <div className="flex items-center gap-4">
          <ThemeToggle />
          <div className="text-right">
            <p className="font-display text-xl" data-testid="player-company">{data.company.name}</p>
            <p className="text-[10px] uppercase tracking-[0.18em] text-accent-blue-light">{data.company.industry}</p>
          </div>
          </div>
        </div>
        <nav className="mx-auto flex max-w-6xl gap-1 overflow-x-auto px-4 sm:px-6" data-testid="player-steps-nav">
          {data.steps.map((s, i) => (
            <button
              key={s.title}
              onClick={() => goTo(i)}
              className={`shrink-0 border-b-2 px-3 py-3 text-[11px] uppercase tracking-[0.16em] transition-colors ${
                i === step ? "border-accent-blue text-white" : "border-transparent text-slate-400 hover:text-white"
              }`}
              ref={i === step ? (el) => el?.scrollIntoView({ block: "nearest", inline: "center" }) : undefined}
              data-testid={`player-step-tab-${i}`}
            >
              {i + 1}. {s.title}
            </button>
          ))}
        </nav>
      </header>
      <main className="mx-auto max-w-6xl px-4 py-10 sm:px-6">
        <p className="eyebrow">Paso {step + 1} de {total}</p>
        <h1 className="mt-2 font-display text-4xl font-medium sm:text-5xl" data-testid="player-step-title">{data.steps[step].title}</h1>
        <div className="mt-8" key={step}>
          <StepView step={step} data={data} token={token} />
        </div>
      </main>
      <NarrationBar
        steps={data.steps}
        step={step}
        playing={playing}
        auto={auto}
        onToggle={toggle}
        onAuto={() => setAuto((a) => !a)}
        onPrev={() => goTo(step - 1)}
        onNext={() => goTo(step + 1)}
      />
    </div>
  );
}

const PlayerMessage = ({ icon: Icon, text, spin }) => (
  <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-[#0B1437] px-6 text-center text-white" data-testid="demo-player-message">
    <InviktusLogo size={40} tone="light" />
    <Icon className={`h-6 w-6 text-accent-blue-light ${spin ? "animate-spin" : ""}`} />
    <p className="max-w-md text-sm text-slate-300">{text}</p>
  </div>
);
